<template>
  <div class="gen-eval">
    <!-- ============ 上传评估表（还原原样式：紫色拖拽上传 + 米黄色状态卡片） ============ -->
    <!-- 每次拖拽上传 = 新增一条评估表 -->
    <div v-if="!isType2" class="gen-eval-upload-card">
      <div class="gen-eval-upload-header">
        <div class="gen-eval-upload-title">
          <i class="el-icon-upload2"></i>
          <span>上传评分表原始数据</span>
        </div>
        <span class="gen-eval-tip">支持 XLS / XLSX 格式，每次上传新增一条评估表</span>
      </div>
      <div class="gen-eval-upload-body">
        <div class="gen-eval-upload-dragger-wrap" :class="{ 'is-loading': uploadLoading }">
          <el-upload
            ref="uploadRef"
            class="gen-eval-upload"
            drag
            action="#"
            :auto-upload="false"
            :limit="1"
            accept=".xls,.xlsx"
            :file-list="fileList"
            :disabled="uploadLoading"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :on-exceed="handleFileExceed"
            :before-upload="() => false"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将评分表原始数据拖到此处，或<em>点击选择文件</em></div>
          </el-upload>
          <!-- 上传中 loading 遮罩 -->
          <div v-if="uploadLoading" class="gen-eval-upload-loading">
            <i class="el-icon-loading"></i>
            <span>正在上传并解析评分表...</span>
          </div>
        </div>
      </div>
      <div class="gen-eval-upload-status-card">
        <div class="gen-eval-upload-status-card__main">{{ evaluationUploadStatus }}</div>
        <div class="gen-eval-upload-status-card__sub">{{ evaluationUploadHint }}</div>
      </div>
    </div>

    <!-- 25型：无拖拽上传，提供「新增25项评估」按钮 -->
    <div v-else-if="hasRrmusmId" class="gen-eval-upload-card">
      <div class="gen-eval-upload-header">
        <div class="gen-eval-upload-title">
          <i class="el-icon-edit-outline"></i>
          <span>25项评分表</span>
          <span v-if="ratingList.length" class="gen-eval-list-count">{{ ratingList.length }} 条</span>
        </div>
        <el-button type="primary" size="small" icon="el-icon-plus" :loading="addRatingLoading" @click="handleAddTwentyFive">新增25项评估</el-button>
      </div>
    </div>

    <!-- 尚无整合主表记录：提示先完成签到整合 -->
    <div v-if="!loading && !hasRrmusmId" class="gen-eval-empty">
      <div class="gen-eval-empty-icon"><i class="el-icon-data-analysis"></i></div>
      <div class="gen-eval-empty-text">暂无考勤整合记录</div>
      <div class="gen-eval-empty-hint">请先在「签到表」模块完成签到识别与整合</div>
    </div>

    <!-- ============ 生成评估表：tab 切换多条（每条数据隔离） ============ -->
    <div v-if="hasRrmusmId && ratingList.length" class="gen-eval-table-card">
      <div class="gen-eval-table-header">
        <div class="gen-eval-table-title">
          <i class="el-icon-data-analysis"></i>
          <span>生成评估表</span>
          <span class="gen-eval-list-count">{{ ratingList.length }} 条</span>
        </div>
        <el-button v-if="ratingList.length" class="gen-eval-clear-btn" size="small" icon="el-icon-delete" :loading="deleteEvalLoading" @click="handleDeleteRating">清空全部评估数据</el-button>
      </div>

      <!-- tab 区：每个 tab = 一条评估，数据完全隔离；用原生 closable 渲染删除键 -->
      <el-tabs v-model="activeRatingId" type="card" class="gen-eval-tabs" @tab-remove="onTabRemove">
        <el-tab-pane
          v-for="(rating, idx) in ratingList"
          :key="rating.rrmusmRatingId"
          :name="String(rating.rrmusmRatingId)"
          closable
        >
          <span slot="label">
            <span class="gen-eval-tab-name">评估{{ idx + 1 }}</span>
            <span class="gen-eval-tab-type" :class="getRatingTypeOf(rating) === 2 ? 'is-25' : 'is-normal'">{{ getRatingTypeOf(rating) === 2 ? '25项' : 'Excel' }}</span>
          </span>

          <!-- ===== 单条评估内容（隔离） ===== -->
          <div class="gen-eval-pane">
            <!-- 普通型(ratingUpType=1)：重新上传 + 删除列 + 导出 + 表格 -->
            <template v-if="getRatingTypeOf(rating) !== 2">
              <div class="gen-eval-pane-ops">
                <el-upload
                  class="gen-eval-pane-upload"
                  action="#"
                  :auto-upload="false"
                  :show-file-list="false"
                  accept=".xls,.xlsx"
                  :on-change="(file) => handleUpdateRatingFile(file, rating)"
                >
                  <el-button size="mini" icon="el-icon-refresh-left" :loading="rating._uploading">重新上传此评估</el-button>
                </el-upload>
                <div class="gen-eval-pane-actions">
                  <template v-if="!deletingColMap[rating.rrmusmRatingId]">
                    <el-button size="mini" icon="el-icon-delete" @click="handleStartDeleteCol(rating)">删除列</el-button>
                  </template>
                  <template v-else>
                    <el-button size="mini" @click="handleCancelDeleteCol(rating)">取消</el-button>
                    <el-button type="danger" size="mini" :disabled="(selectedColMap[rating.rrmusmRatingId] || []).length === 0" :loading="deleteColLoading" @click="handleSubmitDeleteCol(rating)">提交删除</el-button>
                  </template>
                  <el-button type="success" size="mini" icon="el-icon-download" :loading="exportModelLoading" @click="handleExport(rating)">{{ hasRatingModel ? '使用模板导出' : '导出 Excel' }}</el-button>
                </div>
              </div>
              <div v-if="getRatingRows(rating).length" class="gen-eval-pane-table">
                <el-table :data="getRatingRows(rating)" border stripe size="small" :max-height="460" cell-class-name="eval-cell-center">
                  <el-table-column
                    v-for="col in getRatingColumns(rating)"
                    :key="col.prop"
                    :prop="col.prop"
                    :label="col.label"
                    :min-width="col.minWidth"
                    :render-header="(h, ctx) => renderDeleteColHeader(h, ctx, rating)"
                    align="center"
                    header-align="center"
                    show-overflow-tooltip
                  />
                </el-table>
              </div>
              <div v-else class="gen-eval-pane-empty">该评估暂无可展示的评分数据，请重新上传</div>
            </template>

            <!-- 25型(ratingUpType=2)：编辑/保存 + 手填表格 -->
            <template v-else>
              <div class="gen-eval-pane-ops">
                <span class="gen-eval-pane-tip">25项评分表，得分可编辑</span>
                <div class="gen-eval-pane-actions">
                  <template v-if="!editing25Map[rating.rrmusmRatingId]">
                    <el-button type="primary" size="mini" icon="el-icon-edit" @click="handleStartEdit25(rating)">编辑</el-button>
                  </template>
                  <template v-else>
                    <el-button size="mini" @click="handleCancelEdit25(rating)">取消</el-button>
                    <el-button type="primary" size="mini" icon="el-icon-check" :loading="saveLoading" @click="handleSaveTwentyFive(rating)">保存</el-button>
                  </template>
                </div>
              </div>
              <div v-if="getTwentyFiveList(rating).length" class="gen-eval-pane-table">
                <el-table :data="getTwentyFiveList(rating)" border stripe size="small" :max-height="460" cell-class-name="eval-cell-center">
                  <el-table-column prop="ratingIndex" label="序号" width="80" align="center" header-align="center" />
                  <el-table-column prop="ratingItem" label="评价项目" min-width="300" align="left" header-align="center" show-overflow-tooltip />
                  <el-table-column label="得分" width="160" align="center" header-align="center">
                    <template slot-scope="{ row }">
                      <span v-if="!editing25Map[rating.rrmusmRatingId]">{{ row.ratingScore }}</span>
                      <el-input v-else v-model="row.ratingScore" size="mini" class="gen-eval-score-input" />
                    </template>
                  </el-table-column>
                </el-table>
              </div>
              <div v-else class="gen-eval-pane-empty">该评估暂无25项数据</div>
            </template>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 训战业绩板块（非必填，图片上传 + 文字说明，提交后回显；同源主表 rrmusmId） -->
    <div v-if="hasRrmusmId" class="gen-eval-achievement-card">
      <div class="gen-eval-achievement-header">
        <div class="gen-eval-achievement-title">
          <i class="el-icon-picture-outline"></i>
          <span>上传训战业绩</span>
          <span class="gen-eval-achievement-optional">非必填</span>
        </div>
        <span class="gen-eval-achievement-tip">训战期间如有业绩通报，请上传相关业绩数据截图并填写文字说明</span>
      </div>

      <div class="gen-eval-achievement-body">
        <!-- 业绩图片上传（多图，走 COS） -->
        <div class="gen-eval-achievement-row">
          <div class="gen-eval-achievement-label">业绩数据截图</div>
          <div
            v-loading="achievementUploading"
            element-loading-text="正在上传图片..."
            element-loading-background="rgba(255, 255, 255, 0.8)"
            class="gen-eval-achievement-upload"
          >
            <aj-upload
              :key="achievementUploadKey"
              multiple
              :limit="6"
              accept-type="image/*"
              :show-limit-info="true"
              :allow-clear="false"
              :always-show-dropzone="true"
              :upload-request="cosUploader"
              :history-files="achievementImages"
              :max-file-size="10 * 1024 * 1024"
              @upload-success="handleAchievementImgChange"
              @upload-error="handleAchievementImgError"
            >
              <!-- 训战业绩只保留拖拽上传区，覆盖默认「选择图片」按钮 -->
              <template #upload-btn />
            </aj-upload>
          </div>
        </div>

        <!-- 文字说明 -->
        <div class="gen-eval-achievement-row">
          <div class="gen-eval-achievement-label">
            业绩文字说明
            <span class="gen-eval-achievement-label-hint">填写示例：整体训战期间，累计走访XXX家单位，签约合同金额XXX万元；沉淀优秀实践案例XXX个，签约挖掘XXX个项目商机。可见大家能够将课程所学有效运用到实际工作中，并以训代战，产出业绩。</span>
          </div>
          <el-input
            v-model="achievementDes"
            type="textarea"
            :rows="5"
            maxlength="800"
            show-word-limit
            placeholder="请根据实际业务发展情况填写文字说明……"
            class="gen-eval-achievement-textarea"
          />
        </div>

        <!-- 提交 -->
        <div class="gen-eval-achievement-footer">
          <el-button
            class="gen-eval-achievement-submit"
            type="primary"
            icon="el-icon-check"
            :loading="achievementSubmitting"
            :disabled="!canSubmitAchievement"
            @click="handleSubmitAchievement"
          >{{ hasAchievementSaved ? '更新' : '提交' }}</el-button>
          <span v-if="achievementLastSavedText" class="gen-eval-achievement-saved">
            <i class="el-icon-circle-check"></i>{{ achievementLastSavedText }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  addRtReportModelUserSignMergeRating,
  updateRtReportModelUserSignMergeRating,
  addRtReportModelUserSignMergeRatingTwentyFive,
  updateRtReportModelUserSignMergeRatingTwentyFive,
  getRtReportModelUserSignMergeList,
  exportRtReportModelUserSignMergeRating,
  exportRtReportModelUserSignMergeRatingUseModel,
  updateRtReportModelUserSignMergeRatingCol,
  deleteRtReportModelUserSignMergeRating,
  deleteRtReportModelUserSignMergeRatingSingle,
  updateRtReportModelUserSignMergeWordAchievement
} from '@/api/api'
import { uploadToCos } from '@/utils/cosUpload'
import AjUpload from '@/components/ajUpload/ajUpload.vue'

// 解析评分 JSON（兼容对象和字符串，修复后端数字 key 无引号）
function parseMergeJson (val) {
  if (!val) return { columns: [], rows: [] }
  try {
    let data
    if (typeof val !== 'string') {
      data = val
    } else {
      const fixed = val.replace(/([{,]\s*)(\d+)\s*:/g, '$1"$2":')
      data = JSON.parse(fixed)
    }
    // 兼容 headerMap/rowMapList 和 headList/dataList 两种格式
    if (data.headerMap && data.rowMapList) {
      const indices = data.headerMap
        .map((h, i) => (h[0] && String(h[0]).trim()) ? i : -1)
        .filter(i => i >= 0)
      if (!indices.length) return { columns: [], rows: [] }
      const columns = indices.map(i => data.headerMap[i][0] || '')
      const rows = data.rowMapList.map(row => {
        const obj = {}
        indices.forEach((colIdx, i) => { obj['col_' + i] = row[colIdx] || '' })
        return obj
      }).filter(r => Object.values(r).some(v => v !== ''))
      return { columns, rows }
    }
    if (data.headList && data.dataList) {
      const headList = data.headList || []
      const dataList = data.dataList || []
      if (!headList.length) return { columns: [], rows: [] }
      const headerObj = headList[0] || {}
      const colKeys = Object.keys(headerObj).sort((a, b) => Number(a) - Number(b))
      const columns = colKeys.map(k => headerObj[k] || '').filter(h => h)
      const colIndices = colKeys.filter(k => headerObj[k] && String(headerObj[k]).trim())
      const rows = dataList.map(row => {
        const obj = {}
        colIndices.forEach((colIdx, i) => { obj['col_' + i] = row[colIdx] || '' })
        return obj
      }).filter(r => Object.values(r).some(v => v !== ''))
      return { columns, rows }
    }
    return { columns: [], rows: [] }
  } catch {
    return { columns: [], rows: [] }
  }
}

// 根据表头字数动态计算列宽（上限 400px）
function buildColumnConfigs (columns) {
  return columns.map((col, i) => {
    const text = String(col || '').trim()
    let charWidth = 0
    for (const ch of text) {
      charWidth += /[一-鿿]/.test(ch) ? 16 : 10
    }
    const minWidth = Math.min(Math.max(80, charWidth + 64), 400)
    return { prop: 'col_' + i, label: col, minWidth }
  })
}

export default {
  name: 'ReportGenEvaluation',
  components: { AjUpload },
  props: {
    rrmaId: { type: [Number, String], default: null },
    // 默认评分类型（1=普通excel上传，2=25项手填），由父组件弹框打开时通过详情接口获取
    // 决定「新增评估」按钮走 excel 上传 还是 25项新建
    ratingUpType: { type: Number, default: null },
    // 详情数据（含 rtReportModelRatingTwentyFiveList 等），由父组件传入
    detailData: { type: Object, default: null }
  },
  data () {
    return {
      loading: false,
      uploadLoading: false,
      exportModelLoading: false,
      deleteColLoading: false,
      deleteEvalLoading: false,
      saveLoading: false,
      addRatingLoading: false,
      fileList: [],
      // 整合主表记录（含 rrmusmId / ratingModel 等，用于训战业绩同源）
      latestRecord: null,
      // 从列表接口获取的评分类型（决定新增按钮形态）
      localRatingUpType: null,
      // ===== 多条评估（el-tabs） =====
      ratingList: [],
      activeRatingId: '',
      // 普通型 ratingUp 解析缓存（key=rrmusmRatingId）
      ratingParsedCache: {},
      // 25型列表缓存（key=rrmusmRatingId）
      twentyFiveCache: {},
      // 列删除状态：以 rrmusmRatingId 为 key 的 Map
      deletingColMap: {},
      selectedColMap: {},
      // 25型编辑状态：以 rrmusmRatingId 为 key 的 Map
      editing25Map: {},
      twentyFiveBackupMap: {},
      // ===== 训战业绩板块 =====
      achievementImages: [],
      achievementDes: '',
      achievementSavedImages: [],
      achievementSavedDes: '',
      achievementSubmitting: false,
      hasAchievementSaved: false,
      achievementUploadKey: 0,
      achievementUploading: false
    }
  },
  computed: {
    // 默认评分类型：优先列表接口，其次父组件传入
    defaultRatingUpType () {
      return this.localRatingUpType || this.ratingUpType || 1
    },
    hasRatingModel () {
      return this.latestRecord && Number(this.latestRecord.ratingModel) === 2
    },
    // 是否为25型评分（默认类型为2时整页走25项模式）
    isType2 () {
      return this.defaultRatingUpType === 2
    },
    // 上传状态卡片主文案
    evaluationUploadStatus () {
      if (this.uploadLoading) return '正在上传并解析评分表'
      if (this.ratingList.length) return `已上传 ${this.ratingList.length} 份评估表`
      return '待上传评分表'
    },
    // 上传状态卡片副文案
    evaluationUploadHint () {
      if (this.uploadLoading) return '请等待上传完成，系统会自动解析评分数据。'
      if (this.ratingList.length) return '每次上传都会新增一条评估表，下方 tab 可切换查看各条数据。'
      return '支持 XLS / XLSX 格式，每次上传新增一条评估表并自动解析。'
    },
    // 当前记录的签到表识别整合id（评估表、训战业绩同源）
    currentRrmusmId () {
      const id = (this.latestRecord && this.latestRecord.rrmusmId) || (this.detailData && this.detailData.rrmusmId)
      return id || null
    },
    hasRrmusmId () {
      return !!this.currentRrmusmId
    },
    // 提交按钮可用条件：至少有图片或文字说明；且与已保存内容存在差异
    canSubmitAchievement () {
      const hasInput = this.achievementImages.length > 0 || String(this.achievementDes || '').trim().length > 0
      if (!hasInput) return false
      return this.isAchievementDirty
    },
    // 当前内容是否相对已保存内容有改动
    isAchievementDirty () {
      const savedImgUrls = this.achievementSavedImages.map(f => f.url).join(',')
      const curImgUrls = this.achievementImages.map(f => f.url).join(',')
      return savedImgUrls !== curImgUrls || this.achievementSavedDes !== this.achievementDes
    },
    // 保存成功后的回显提示文案
    achievementLastSavedText () {
      if (!this.hasAchievementSaved) return ''
      return '训战业绩已保存'
    },
    // COS 上传函数（供模板 AjUpload 的 upload-request 使用）
    cosUploader () {
      // 包装 uploadToCos：感知上传起止，切换 loading 状态
      return async (rawFile, ctx) => {
        this.achievementUploading = true
        try {
          const result = await uploadToCos(rawFile, ctx)
          return result
        } finally {
          // AjUpload 的 processFiles 是循环串行调用，每个文件上传完会触发一次
          // 这里在单个文件上传结束后先短暂保持 loading，由 upload-success 事件统一关闭
        }
      }
    }
  },
  watch: {
    rrmaId: {
      handler (val) {
        if (val) this.fetchList()
      },
      immediate: true
    }
  },
  methods: {
    // 判断某条评估的类型（1普通 / 2为25项）
    getRatingTypeOf (rating) {
      if (!rating) return this.defaultRatingUpType
      // 子记录自身可能带 ratingUpType；否则用主表默认类型
      const t = rating.ratingUpType
      return t != null ? Number(t) : this.defaultRatingUpType
    },

    // 查询整合主记录 → 渲染多条评估 + 回显训战业绩
    async fetchList () {
      if (!this.rrmaId) return
      this.loading = true
      try {
        const res = await getRtReportModelUserSignMergeList({ rrmaId: this.rrmaId })
        // 兼容 data 为单条对象或数组（一个申请只有一条整合主记录）
        let record = null
        if (Array.isArray(res.data)) {
          record = res.data.length ? res.data[res.data.length - 1] : null
        } else if (res.data && (res.data.rrmusmId !== undefined || res.data.ratingUpType !== undefined)) {
          record = res.data
        }
        this.latestRecord = record
        const type = record ? (record.ratingUpType || null) : null
        this.localRatingUpType = type

        // 多条评估子记录
        const list = (record && record.rtReportModelUserSignMergeRatingList) || []
        this.ratingList = list.map(item => ({ ...item, _uploading: false }))
        this.ratingParsedCache = {}
        this.twentyFiveCache = {}
        this.deletingColMap = {}
        this.selectedColMap = {}
        this.editing25Map = {}
        this.twentyFiveBackupMap = {}
        // 默认激活第一条
        this.activeRatingId = this.ratingList.length ? String(this.ratingList[0].rrmusmRatingId) : ''

        // 回显训战业绩（同源 rrmusmId 记录上的 achievementDes / achievementImage 字段）
        this.restoreAchievement(record)
      } catch (e) {
        this.latestRecord = null
        this.localRatingUpType = null
        this.ratingList = []
        this.activeRatingId = ''
        this.ratingParsedCache = {}
        this.twentyFiveCache = {}
        this.deletingColMap = {}
        this.selectedColMap = {}
        this.editing25Map = {}
        this.twentyFiveBackupMap = {}
        this.resetAchievement()
      } finally {
        this.loading = false
      }
    },

    // ===== 单条评分数据解析（普通型，带缓存） =====
    getRatingParsed (rating) {
      if (!rating || !rating.rrmusmRatingId) return { columns: [], rows: [] }
      const id = rating.rrmusmRatingId
      if (!this.ratingParsedCache[id]) {
        this.$set(this.ratingParsedCache, id, parseMergeJson(rating.ratingUp))
      }
      return this.ratingParsedCache[id]
    },
    getRatingColumns (rating) {
      return buildColumnConfigs(this.getRatingParsed(rating).columns)
    },
    getRatingRows (rating) {
      return this.getRatingParsed(rating).rows
    },

    // ===== 25型列表（带缓存） =====
    getTwentyFiveList (rating) {
      if (!rating || !rating.rrmusmRatingId) return []
      const id = rating.rrmusmRatingId
      if (!this.twentyFiveCache[id]) {
        let arr = []
        if (rating.ratingUp) {
          try {
            const parsed = typeof rating.ratingUp === 'string' ? JSON.parse(rating.ratingUp) : rating.ratingUp
            if (Array.isArray(parsed)) arr = parsed
          } catch (e) { /* 忽略 */ }
        }
        // 兼容主表旧字段（rtReportModelRatingTwentyFiveList）的迁移数据
        if (!arr.length && rating.rtReportModelRatingTwentyFiveList) {
          arr = rating.rtReportModelRatingTwentyFiveList
        }
        this.$set(this.twentyFiveCache, id, arr.map(item => ({ ...item })))
      }
      return this.twentyFiveCache[id]
    },

    // ===== tab 切换：清掉该条无关状态（可选优化，保持简单这里仅占位） =====
    handleTabClick () {},

    // ===== 拖拽上传（每次上传 = 新增一条评估表） =====
    async handleFileChange (file) {
      if (!file || !file.raw) return
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      if (!['.xls', '.xlsx'].includes(ext)) {
        this.$message.warning('仅支持 XLS、XLSX 格式的 Excel 文件')
        this.handleFileRemove()
        return
      }
      if (!this.hasRrmusmId) {
        this.$message.warning('暂无考勤整合记录，请先完成签到整合')
        this.handleFileRemove()
        return
      }
      this.uploadLoading = true
      this.$emit('busy-change', true)
      try {
        // 新增评分用整合主表 rrmusmId；接口返回新增的评估记录对象
        const res = await addRtReportModelUserSignMergeRating({ rrmusmId: this.currentRrmusmId, file: file.raw })
        this.$message.success('评估表新增成功')
        const newRecord = res && res.data
        // 后端 add 接口可能未回填主键（rrmusmRatingId 为 null），
        // 有主键则就地 upsert，无主键则回退到全量拉取以拿到完整数据（含真实主键）
        if (newRecord && newRecord.rrmusmRatingId) {
          this.upsertRatingRecord(newRecord)
        } else {
          await this.fetchList()
        }
        // 新增后激活最后一条
        if (this.ratingList.length) this.activeRatingId = String(this.ratingList[this.ratingList.length - 1].rrmusmRatingId)
        this.$emit('progress-change')
      } catch (err) {
        this.$message.error(err.message || '上传评分表失败，请稍后重试')
      } finally {
        this.uploadLoading = false
        this.$emit('busy-change', false)
        this.handleFileRemove()
      }
    },
    handleFileRemove () {
      this.fileList = []
      if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
    },
    handleFileExceed () {
      this.$message.warning('请先完成当前文件上传')
    },

    // 就地把新增/编辑返回的评估记录合并进 ratingList（避免全量 fetchList）
    upsertRatingRecord (ratingRecord) {
      if (!ratingRecord || !ratingRecord.rrmusmRatingId) return
      const idx = this.ratingList.findIndex(r => r.rrmusmRatingId === ratingRecord.rrmusmRatingId)
      const normalized = { ...ratingRecord, _uploading: false }
      if (idx >= 0) {
        this.$set(this.ratingList, idx, normalized)
        this.$delete(this.ratingParsedCache, ratingRecord.rrmusmRatingId)
        this.$delete(this.twentyFiveCache, ratingRecord.rrmusmRatingId)
      } else {
        this.ratingList.push(normalized)
      }
    },
    // 25型新增：创建空表（初始25项）
    async handleAddTwentyFive () {
      if (!this.hasRrmusmId) {
        this.$message.warning('暂无考勤整合记录，请先完成签到整合')
        return
      }
      this.addRatingLoading = true
      this.$emit('busy-change', true)
      try {
        // 构造初始空25项（序号1-25，空项目，空得分）
        const emptyList = Array.from({ length: 25 }, (_, i) => ({
          ratingIndex: i + 1,
          ratingItem: '',
          ratingScore: null
        }))
        await addRtReportModelUserSignMergeRatingTwentyFive({
          rrmusmId: this.currentRrmusmId,
          ratingUp: JSON.stringify(emptyList)
        })
        this.$message.success('25项评估记录新增成功')
        await this.fetchList()
        if (this.ratingList.length) this.activeRatingId = String(this.ratingList[this.ratingList.length - 1].rrmusmRatingId)
        this.$emit('progress-change')
      } catch (err) {
        this.$message.error(err.message || '新增评估失败，请稍后重试')
      } finally {
        this.addRatingLoading = false
        this.$emit('busy-change', false)
      }
    },

    // ===== 重新上传单条评分（普通型，覆盖该条 ratingUp） =====
    async handleUpdateRatingFile (file, rating) {
      if (!file || !file.raw || !rating || !rating.rrmusmRatingId) return
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      if (!['.xls', '.xlsx'].includes(ext)) {
        this.$message.warning('仅支持 XLS、XLSX 格式的 Excel 文件')
        return
      }
      this.$set(rating, '_uploading', true)
      this.$emit('busy-change', true)
      try {
        // 编辑单条评分用 rrmusmRatingId；接口返回更新后的评估记录对象
        const res = await updateRtReportModelUserSignMergeRating({ rrmusmRatingId: rating.rrmusmRatingId, file: file.raw })
        this.$message.success('该评估已更新')
        const updated = res && res.data
        // 返回数据有主键则就地更新，无主键则清缓存后回退全量拉取
        if (updated && updated.rrmusmRatingId) {
          this.upsertRatingRecord(updated)
        } else {
          this.$delete(this.ratingParsedCache, rating.rrmusmRatingId)
          await this.fetchList()
          // 更新场景保持当前 tab（fetchList 默认会重置为第一条）
          this.activeRatingId = String(rating.rrmusmRatingId)
        }
        this.$emit('progress-change')
      } catch (err) {
        this.$message.error(err.message || '更新评估失败，请稍后重试')
      } finally {
        this.$set(rating, '_uploading', false)
        this.$emit('busy-change', false)
      }
    },

    // ===== 删除列（按 rrmusmRatingId） =====
    handleStartDeleteCol (rating) {
      this.$set(this.deletingColMap, rating.rrmusmRatingId, true)
      this.$set(this.selectedColMap, rating.rrmusmRatingId, [])
    },
    handleCancelDeleteCol (rating) {
      this.$set(this.deletingColMap, rating.rrmusmRatingId, false)
      this.$set(this.selectedColMap, rating.rrmusmRatingId, [])
    },
    toggleColSelect (ratingId, idx) {
      const arr = this.selectedColMap[ratingId] || []
      const pos = arr.indexOf(idx)
      if (pos >= 0) {
        arr.splice(pos, 1)
      } else {
        arr.push(idx)
      }
      this.$set(this.selectedColMap, ratingId, [...arr])
    },
    async handleSubmitDeleteCol (rating) {
      if (!rating || !rating.rrmusmRatingId) {
        this.$message.warning('暂无评分数据')
        return
      }
      const selected = this.selectedColMap[rating.rrmusmRatingId] || []
      if (!selected.length) {
        this.$message.warning('请至少选择一列')
        return
      }
      this.deleteColLoading = true
      this.$emit('busy-change', true)
      try {
        // 删除列用 rrmusmRatingId
        await updateRtReportModelUserSignMergeRatingCol({
          rrmusmRatingId: rating.rrmusmRatingId,
          indexJson: JSON.stringify(selected)
        })
        this.$message.success('删除列成功')
        this.$set(this.deletingColMap, rating.rrmusmRatingId, false)
        this.$set(this.selectedColMap, rating.rrmusmRatingId, [])
        await this.fetchList()
        this.$emit('progress-change')
      } catch (e) {
        this.$message.error(e.message || '删除列失败，请稍后重试')
      } finally {
        this.deleteColLoading = false
        this.$emit('busy-change', false)
      }
    },

    // 自定义表头：非删除模式直接返回文本，删除模式渲染复选框+列名
    renderDeleteColHeader (h, { column, $index }, rating) {
      if (!rating || !this.deletingColMap[rating.rrmusmRatingId]) {
        return h('span', column.label)
      }
      const idx = $index
      const selected = this.selectedColMap[rating.rrmusmRatingId] || []
      const checked = selected.includes(idx)
      return h('div', { style: 'display:flex;align-items:center;justify-content:center;gap:4px;' }, [
        h('el-checkbox', {
          props: { value: checked },
          on: { change: () => this.toggleColSelect(rating.rrmusmRatingId, idx) }
        }),
        h('span', column.label)
      ])
    },

    // ===== 25型编辑/保存（按 rrmusmRatingId） =====
    handleStartEdit25 (rating) {
      // 进入编辑前先确保缓存已构建，再备份
      const list = this.getTwentyFiveList(rating)
      this.$set(this.twentyFiveBackupMap, rating.rrmusmRatingId, list.map(item => ({ ...item })))
      this.$set(this.editing25Map, rating.rrmusmRatingId, true)
    },
    handleCancelEdit25 (rating) {
      const backup = this.twentyFiveBackupMap[rating.rrmusmRatingId] || []
      this.$set(this.twentyFiveCache, rating.rrmusmRatingId, backup.map(item => ({ ...item })))
      this.$set(this.editing25Map, rating.rrmusmRatingId, false)
    },
    async handleSaveTwentyFive (rating) {
      if (!rating || !rating.rrmusmRatingId) {
        this.$message.warning('暂无评分记录，无法保存')
        return
      }
      const list = this.getTwentyFiveList(rating)
      this.saveLoading = true
      this.$emit('busy-change', true)
      try {
        // 编辑25型用 rrmusmRatingId
        await updateRtReportModelUserSignMergeRatingTwentyFive({
          rrmusmRatingId: rating.rrmusmRatingId,
          ratingUp: JSON.stringify(list)
        })
        this.$message.success('保存成功')
        this.$set(this.editing25Map, rating.rrmusmRatingId, false)
        this.$emit('progress-change')
      } catch (e) {
        this.$message.error(e.message || '保存失败，请稍后重试')
      } finally {
        this.saveLoading = false
        this.$emit('busy-change', false)
      }
    },

    // ===== 导出（按 rrmusmRatingId；25型不可导出） =====
    async handleExport (rating) {
      if (!rating || !rating.rrmusmRatingId) {
        this.$message.warning('暂无可导出的评分数据')
        return
      }
      if (this.getRatingTypeOf(rating) === 2) {
        this.$message.warning('25项评估表不支持导出')
        return
      }
      this.exportModelLoading = true
      try {
        const params = { rrmusmRatingId: rating.rrmusmRatingId }
        const result = this.hasRatingModel
          ? await exportRtReportModelUserSignMergeRatingUseModel(params)
          : await exportRtReportModelUserSignMergeRating(params)
        this.$message.success('导出成功：' + result.fileName)
      } catch (e) {
        this.$message.error(e.message || '导出失败，请稍后重试')
      } finally {
        this.exportModelLoading = false
      }
    },

    // ===== 清空全部评估数据（主表级） =====
    async handleDeleteRating () {
      const rrmusmId = this.currentRrmusmId
      if (!rrmusmId) {
        this.$message.warning('暂无可清空的评估数据')
        return
      }
      try {
        await this.$confirm('确认清空全部评估数据？考勤和报告数据不会被清空。', '清空评估数据', { type: 'warning' })
        this.deleteEvalLoading = true
        this.$emit('busy-change', true)
        await deleteRtReportModelUserSignMergeRating({ rrmusmId })
        this.$message.success('评估数据已清空')
        await this.fetchList()
        this.$emit('progress-change')
      } catch (e) {
        if (e !== 'cancel') this.$message.error(e.message || '清空失败，请稍后重试')
      } finally {
        this.deleteEvalLoading = false
        this.$emit('busy-change', false)
      }
    },

    // ===== 删除单条评估（tab 上的删除键） =====
    async handleDeleteRatingSingle (rating, idx) {
      if (!rating || !rating.rrmusmRatingId) return
      try {
        await this.$confirm(`确认删除「评估${idx + 1}」？`, '删除评估', { type: 'warning' })
      } catch (e) {
        return // 用户取消
      }
      this.$emit('busy-change', true)
      try {
        await deleteRtReportModelUserSignMergeRatingSingle({ rrmusmRatingId: rating.rrmusmRatingId })
        this.$message.success('删除成功')
        // 就地从列表移除该条 + 清缓存
        const pos = this.ratingList.findIndex(r => r.rrmusmRatingId === rating.rrmusmRatingId)
        if (pos >= 0) this.ratingList.splice(pos, 1)
        this.$delete(this.ratingParsedCache, rating.rrmusmRatingId)
        this.$delete(this.twentyFiveCache, rating.rrmusmRatingId)
        // 删除后激活剩余的第一条（若有）
        this.activeRatingId = this.ratingList.length ? String(this.ratingList[0].rrmusmRatingId) : ''
        this.$emit('progress-change')
      } catch (err) {
        this.$message.error(err.message || '删除失败，请稍后重试')
      } finally {
        this.$emit('busy-change', false)
      }
    },
    // el-tabs 原生 closable 删除事件桥接：targetName 即 tab 的 name（String(ratingId)）
    onTabRemove (targetName) {
      const idx = this.ratingList.findIndex(r => String(r.rrmusmRatingId) === String(targetName))
      if (idx < 0) return
      this.handleDeleteRatingSingle(this.ratingList[idx], idx)
    },

    // ===== 训战业绩板块方法 =====

    // 图片列表变化回调（AjUpload 上传/删除后触发，回传最新快照数组）
    handleAchievementImgChange (snapshot) {
      this.achievementImages = (snapshot || []).map(f => ({
        url: f.url,
        name: f.name,
        size: f.size,
        extension: f.extension,
        type: f.type || 'image',
        storageKey: f.storageKey || ''
      }))
      // upload-success 事件触发于所有文件上传完成之后，关闭 loading
      this.achievementUploading = false
    },
    // 上传失败回调：关闭 loading 并提示
    handleAchievementImgError () {
      this.achievementUploading = false
    },

    // 从接口记录回显训战业绩
    // 注：后端将业绩字段(achievementDes/achievementImage)存于主表 wordTextUser JSON 内
    restoreAchievement (record) {
      if (!record) {
        this.resetAchievement()
        return
      }
      // 解析 wordTextUser JSON，读取业绩字段
      let des = ''
      let rawImg = ''
      if (record.wordTextUser) {
        try {
          const obj = typeof record.wordTextUser === 'string'
            ? JSON.parse(record.wordTextUser)
            : record.wordTextUser
          des = obj.achievementDes || ''
          rawImg = obj.achievementImage || ''
        } catch (e) { /* 忽略解析失败 */ }
      }
      const images = this.parseAchievementImages(rawImg)
      this.achievementDes = des
      this.achievementImages = images
      // 同步快照，用于判断是否已保存/是否有改动
      this.achievementSavedDes = des
      this.achievementSavedImages = images.map(f => ({ ...f }))
      this.hasAchievementSaved = !!(des || images.length)
      // 强制刷新上传组件以正确回显
      this.achievementUploadKey++
    },

    resetAchievement () {
      this.achievementDes = ''
      this.achievementImages = []
      this.achievementSavedDes = ''
      this.achievementSavedImages = []
      this.hasAchievementSaved = false
      this.achievementUploadKey++
    },

    // 解析后端 achievementImage 字段为图片数组
    // 兼容：逗号分隔URL字符串 / JSON数组字符串 / 已是数组
    parseAchievementImages (raw) {
      if (!raw) return []
      // 尝试 JSON 数组
      if (typeof raw === 'string' && raw.trim().startsWith('[')) {
        try {
          const arr = JSON.parse(raw)
          if (Array.isArray(arr)) {
            return arr.filter(Boolean).map((item, idx) => {
              const url = typeof item === 'string' ? item : (item.url || item)
              return this.buildImageObj(url, idx)
            })
          }
        } catch (e) {
          // 降级为逗号分隔处理
        }
      }
      // 逗号分隔的 URL 字符串
      return String(raw)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map((url, idx) => this.buildImageObj(url, idx))
    },

    buildImageObj (url, idx) {
      const name = url ? String(url).split('/').pop().split('?')[0] : `业绩截图${idx + 1}`
      const ext = (name.split('.').pop() || '').toLowerCase()
      return {
        url,
        name,
        size: 0,
        extension: ext,
        type: 'image',
        storageKey: ''
      }
    },

    // 提交训战业绩：图片URL逗号拼接 + 文字说明
    async handleSubmitAchievement () {
      const rrmusmId = this.currentRrmusmId
      if (!rrmusmId) {
        this.$message.warning('暂无评分记录，无法提交训战业绩')
        return
      }
      const imgUrl = this.achievementImages.map(f => f.url).filter(Boolean).join(',')
      this.achievementSubmitting = true
      this.$emit('busy-change', true)
      try {
        await updateRtReportModelUserSignMergeWordAchievement({
          rrmusmId,
          achievementDes: this.achievementDes || '',
          achievementImage: imgUrl
        })
        // 提交成功后同步已保存快照（保持回显 + 改动判断一致）
        this.achievementSavedDes = this.achievementDes
        this.achievementSavedImages = this.achievementImages.map(f => ({ ...f }))
        this.hasAchievementSaved = true
        this.$message.success('训战业绩已保存')
        this.$emit('progress-change')
      } catch (e) {
        this.$message.error(e.message || '训战业绩保存失败，请稍后重试')
      } finally {
        this.achievementSubmitting = false
        this.$emit('busy-change', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// ===== 上传评分表卡片（紫色系，还原原样式） =====
.gen-eval-upload-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background:
    radial-gradient(circle at 92% 8%, rgba(139, 92, 246, 0.06), transparent 38%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(250, 245, 255, 0.94));
}

.gen-eval-upload-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #faf5ff, #ffffff);
  border-bottom: 1px solid rgba(139, 92, 246, 0.14);
}

.gen-eval-upload-title {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  flex-shrink: 1;
  min-width: 0;

  i { color: #7c3aed; font-size: 15px; flex-shrink: 0; }
}

.gen-eval-tip {
  font-size: 10px;
  color: #94a3b8;
  flex-shrink: 0;
}

.gen-eval-upload-body {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;

  ::v-deep .el-upload-dragger {
    width: 100%;
    height: 130px;
    line-height: normal;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border-color: rgba(139, 92, 246, 0.32);
    background: linear-gradient(180deg, #ffffff, #faf8ff);

    .el-icon-upload {
      margin: 0;
      font-size: 36px;
      line-height: 36px;
      color: #7c3aed;
    }

    .el-upload__text {
      font-size: 12px;
      margin-top: 6px;
      color: #606266;
      text-align: center;
      line-height: 1.5;
      padding: 0 16px;

      em { color: #7c3aed; }
    }
  }
}

.gen-eval-upload {
  ::v-deep .el-upload-list { display: none; }
}

// 拖拽区包裹层：定位上下文，供 loading 遮罩绝对定位
.gen-eval-upload-dragger-wrap {
  position: relative;
  width: 100%;

  // 上传中：拖拽区降透明度 + 禁止点击
  &.is-loading {
    ::v-deep .el-upload-dragger {
      opacity: 0.55;
      pointer-events: none;
    }
  }
}

// 上传中 loading 遮罩（紫色系，覆盖拖拽区）
.gen-eval-upload-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 6px;
  background: rgba(245, 243, 255, 0.78);
  backdrop-filter: blur(2px);
  z-index: 5;

  i {
    font-size: 30px;
    color: #7c3aed;
    animation: evalUploading 1.1s linear infinite;
  }
  span {
    font-size: 13px;
    font-weight: 600;
    color: #5b21b6;
  }

  @keyframes evalUploading {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
}

// 米黄色状态卡片（还原原样式）
.gen-eval-upload-status-card {
  margin: 0 16px 14px;
  padding: 12px 14px;
  border: 1px solid rgba(139, 92, 246, 0.18);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(250, 245, 255, 0.94), rgba(255, 255, 255, 0.98));
}

.gen-eval-upload-status-card__main {
  color: #5b21b6;
  font-size: 14px;
  font-weight: 850;
}

.gen-eval-upload-status-card__sub {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
}

// ===== 生成评估表卡片（tab 切换多条，紫色系） =====
.gen-eval-table-card {
  margin-top: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.gen-eval-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: linear-gradient(135deg, #faf5ff, #ffffff);
  border-bottom: 1px solid rgba(139, 92, 246, 0.14);
}

.gen-eval-table-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;

  i { color: #7c3aed; font-size: 17px; }
}

.gen-eval-list-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #7c3aed;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
}

// 清空全部按钮：柔和红色描边款（危险操作但不喧宾夺主）
.gen-eval-clear-btn {
  color: #e11d48 !important;
  background: #fff5f7 !important;
  border-color: #fecdd3 !important;

  &:hover,
  &:focus {
    color: #fff !important;
    background: #e11d48 !important;
    border-color: #e11d48 !important;
  }
}

// el-tabs 多条切换（紫色系，精致圆角标签 + 明显激活态）
.gen-eval-tabs {
  padding: 14px 16px 12px;

  ::v-deep .el-tabs__header {
    margin: 0 0 14px;
    border-bottom: none;
  }
  ::v-deep .el-tabs__nav {
    border: none;
    display: inline-flex;
    gap: 8px;
  }
  ::v-deep .el-tabs__item {
    height: 34px;
    line-height: 34px;
    padding: 0 16px;
    border: 1px solid #ece3fb !important;
    border-radius: 17px;
    margin-right: 0;
    background: #faf8ff;
    color: #8b5cf6;
    font-size: 13px;
    transition: all 0.2s ease;

    &:hover {
      color: #7c3aed;
      border-color: #d8c7f8;
      background: #f5f0ff;
    }
    &.is-active {
      background: linear-gradient(135deg, #8b5cf6, #7c3aed);
      color: #fff;
      border-color: transparent !important;
      box-shadow: 0 3px 10px rgba(124, 58, 237, 0.3);
      .gen-eval-tab-type { background: rgba(255, 255, 255, 0.25); color: #fff; }
    }
    // 原生 closable 删除键样式
    .el-icon-close {
      margin-left: 6px;
      font-size: 13px;
      width: 18px;
      height: 18px;
      line-height: 18px;
      text-align: center;
      border-radius: 50%;
      color: #94a3b8;
      background: transparent;
      transition: all 0.15s ease;
      &:hover {
        color: #fff;
        background: #ef4444;
      }
    }
    &.is-active .el-icon-close {
      color: rgba(255, 255, 255, 0.85);
      &:hover { background: #ef4444; color: #fff; }
    }
  }
  ::v-deep .el-tabs__content { overflow: visible; }
}

.gen-eval-tab-name {
  font-weight: 600;
  margin-right: 6px;
}
.gen-eval-tab-type {
  font-size: 11px;
  font-weight: 500;
  padding: 1px 7px;
  border-radius: 9px;
  transition: all 0.2s ease;
  &.is-normal { color: #2563eb; background: #eef4ff; }
  &.is-25 { color: #7c3aed; background: #efe7fb; }
}

// 单条评估面板（数据隔离）
.gen-eval-pane {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 8px;
}

// 操作区：左侧主操作 + 右侧次操作，整体居中分组
.gen-eval-pane-ops {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 12px;
  background: linear-gradient(135deg, #faf8ff, #fdfbff);
  border: 1px solid #efe7fb;
  border-radius: 8px;
}

.gen-eval-pane-upload {
  ::v-deep .el-upload-list { display: none; }
}

.gen-eval-pane-tip {
  font-size: 12px;
  color: #8b5cf6;
  font-weight: 500;
}

.gen-eval-pane-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.gen-eval-pane-table {
  overflow-x: auto;
}

.gen-eval-pane-empty {
  padding: 24px;
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  background: #f8fafc;
  border-radius: 6px;
}

// 单元格内容水平垂直居中
::v-deep .eval-cell-center {
  .cell {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

// 可编辑得分输入框
.gen-eval-score-input {
  ::v-deep .el-input__inner {
    text-align: center;
  }
}

// 空状态（无整合记录）
.gen-eval-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 44px 24px 36px;
}

.gen-eval-empty-icon {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f5f3ff, #ede9fe);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  i { font-size: 28px; color: #7c3aed; }
}

.gen-eval-empty-text {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
}

.gen-eval-empty-hint {
  font-size: 12px;
  color: #94a3b8;
}

// ===== 训战业绩板块 =====
.gen-eval-achievement-card {
  margin-top: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  background:
    radial-gradient(circle at 92% 8%, rgba(16, 185, 129, 0.06), transparent 38%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(240, 253, 250, 0.94));
}

.gen-eval-achievement-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #f0fdfa, #ffffff);
  border-bottom: 1px solid rgba(16, 185, 129, 0.16);
}

.gen-eval-achievement-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;

  i { color: #0d9488; font-size: 17px; }
}

.gen-eval-achievement-optional {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 8px;
  margin-left: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 9px;
}

.gen-eval-achievement-tip {
  font-size: 12px;
  color: #94a3b8;
  flex-shrink: 0;
}

.gen-eval-achievement-body {
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gen-eval-achievement-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gen-eval-achievement-label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.gen-eval-achievement-label-hint {
  font-size: 12px;
  font-weight: 400;
  color: #94a3b8;
  line-height: 1.6;
  padding: 6px 10px;
  background: #f8fafc;
  border-left: 3px solid #0d9488;
  border-radius: 0 6px 6px 0;
}

.gen-eval-achievement-upload {
  // 训战业绩只保留拖拽上传区，隐藏整个工具栏（选择按钮/清空/计数）
  // 拖拽区独立于工具栏，点击/拖拽上传能力不受影响
  ::v-deep .aj-upload__toolbar {
    display: none !important;
  }
}

.gen-eval-achievement-textarea {
  ::v-deep .el-textarea__inner {
    line-height: 1.6;
  }
}

.gen-eval-achievement-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 4px;
  border-top: 1px dashed #e2e8f0;
}

// 提交/更新按钮：hover/focus/active 保持深色背景 + 白字，避免被默认浅色覆盖导致看不见
.gen-eval-achievement-submit.el-button--primary:hover,
.gen-eval-achievement-submit.el-button--primary:focus,
.gen-eval-achievement-submit.el-button--primary:active {
  background: linear-gradient(135deg, #2563eb 0%, #1d7fe8 100%) !important;
  border-color: transparent !important;
  color: #ffffff !important;
  box-shadow: 0 6px 14px rgba(29, 127, 232, 0.28) !important;
}
.gen-eval-achievement-submit.el-button--primary.is-disabled,
.gen-eval-achievement-submit.el-button--primary.is-disabled:hover {
  opacity: 0.5;
  background: linear-gradient(135deg, #2563eb 0%, #1d7fe8 100%) !important;
  color: #ffffff !important;
}

.gen-eval-achievement-saved {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #0d9488;

  i { font-size: 14px; }
}
</style>
